import moduleInfo from '../module.json';
import type { AppRuntime } from '@ayocore/platform/client/runtime/AppRuntime';
import {
  IconBuilding,
  IconBuildingCommunity,
  IconUsers,
  IconTarget,
  IconEye,
  IconChartBar,
  IconBriefcase,
  IconDatabase,
  IconSettings,
  IconCategory,
  IconTag,
  IconReportMoney,
  IconMap,
  IconBroadcast,
  IconChecklist,
} from '@tabler/icons-react';
import { lazy } from 'react';
import { registerCrmContextMenuItems } from './contextMenuItems';
import { registerCrmActivityExtensions } from './activity-extensions';
import { registerCrmWelcomeExtensions } from './extensions/CrmWelcomeExtension';
import { registerPartnerLeadCreationExtensions } from './extensions/PartnerLeadCreationExtension';

/**
 * Бутстрап модуля CRM (клиент)
 * Регистрирует навигацию через рантайм.
 */
export async function setup(app: AppRuntime) {
  const crm = app.useModule(moduleInfo.name, {
    version: moduleInfo.version,
    description: moduleInfo.description,
    navigationOrder: 10,
  });

  registerCrmWelcomeExtensions();
  registerPartnerLeadCreationExtensions();
  // CRM-расширения Activity: дескрипторы сущностей, действия шапки, команды чата
  registerCrmActivityExtensions();
  // Вкладка «Комментарии» не регистрируется глобально — комментарии и так доступны
  // в Activity-панели. Инстанс может включить точечно через
  // registerCrmCommentsExtensions({ deal: true, partner: true, lead: true }).
  registerCrmContextMenuItems(crm);

  const groupCRM = crm.createNavigationGroup({
    key: 'crm',
    label: 'CRM',
    icon: IconBriefcase,
    order: 1,
  });
  crm.useNavigation([
    {
      icon: IconDatabase,
      label: 'Activity Backfill',
      url: '/crm/activity/backfill',
      component: lazy(() => import('./components/ActivityBackfill/ActivityBackfillPage')),
      hiddenFromMenu: true,
    },
    {
      group: groupCRM,
      icon: IconBuilding,
      label: 'Партнеры',
      url: '/crm/partners',
      component: lazy(() => import('./components/Partners/PartnersList')),
    },
    {
      group: groupCRM,
      icon: IconUsers,
      label: 'Контактные лица',
      url: '/crm/contacts',
      component: lazy(() => import('./components/ContactPersons/ContactPersonsList')),
    },
    {
      group: groupCRM,
      icon: IconTarget,
      label: 'Лиды',
      url: '/crm/leads',
      component: lazy(() => import('./components/Leads/LeadsList')),
    },
    {
      group: groupCRM,
      icon: IconBriefcase,
      label: 'Сделки',
      url: '/crm/deals',
      component: lazy(() => import('./components/Deals/DealsList')),
    },
    {
      group: groupCRM,
      icon: IconChecklist,
      label: 'Задачи',
      url: '/crm/tasks',
      component: lazy(() => import('./components/tasks/views/CrmTasksListPage')),
    },
    {
      icon: IconEye,
      label: 'Просмотр сделки',
      url: '/crm/deals/:id',
      component: lazy(() =>
        import('./components/Deals/views/DealViewPage').then(m => ({ default: m.DealViewPage }))
      ),
      hiddenFromMenu: true,
    },
    {
      icon: IconEye,
      label: 'Просмотр партнера',
      url: '/crm/partners/:id',
      component: lazy(() =>
        import('./components/Partners/views/PartnerViewPage').then(m => ({
          default: m.PartnerViewPage,
        }))
      ),
      hiddenFromMenu: true, // Don't show in menu since it requires an ID parameter
    },
    {
      icon: IconEye,
      label: 'Просмотр лида',
      url: '/crm/leads/:id',
      component: lazy(() =>
        import('./components/Leads/views/LeadViewPage').then(m => ({ default: m.LeadViewPage }))
      ),
      hiddenFromMenu: true,
    },
    // Отчёты CRM
    {
      group: groupCRM,
      icon: IconReportMoney,
      label: 'Планы продаж',
      url: '/crm/sales-plans',
      component: lazy(() => import('./components/SalesPlans/SalesPlansList')),
    },
    {
      icon: IconEye,
      label: 'Просмотр плана продаж',
      url: '/crm/sales-plans/:id',
      component: lazy(() =>
        import('./components/SalesPlans/SalesPlanViewPage').then(m => ({
          default: m.SalesPlanViewPage,
        }))
      ),
      hiddenFromMenu: true,
    },
    {
      group: groupCRM,
      icon: IconChartBar,
      label: 'Отчёты',
      url: '/crm/reports',
      component: lazy(() => import('./components/Reports/CrmReportsPage')),
    },
    {
      icon: IconChartBar,
      label: 'Анализ по сделкам',
      url: '/crm/reports/deal_reports',
      component: lazy(() => import('./components/Reports/DealReport')),
      hiddenFromMenu: true,
    },
    {
      icon: IconChartBar,
      label: 'Анализ по лидам',
      url: '/crm/reports/lead_dashboard',
      component: lazy(() => import('./components/Reports/lead_dashboard/LeadDashboard')),
      hiddenFromMenu: true,
    },
    {
      icon: IconChartBar,
      label: 'Анализ по партнёрам',
      url: '/crm/reports/partner_reports',
      component: lazy(() => import('./components/Reports/PartnerReport')),
      hiddenFromMenu: true,
    },
    {
      icon: IconChartBar,
      label: 'Анализ по менеджерам',
      url: '/crm/reports/manager_reports',
      component: lazy(() => import('./components/Reports/ManagerReport')),
      hiddenFromMenu: true,
    },
    {
      icon: IconChartBar,
      label: 'Поиск дублей',
      url: '/crm/reports/duplicates',
      component: lazy(() => import('./components/Reports/PartnerDuplicatesReport')),
      hiddenFromMenu: true,
    },
    // Настройки CRM
    {
      group: groupCRM,
      icon: IconSettings,
      label: 'Настройки',
      url: '/crm/settings',
      component: lazy(() => import('./components/Settings/CrmSettingsPage')),
    },
    {
      icon: IconBuildingCommunity,
      label: 'Филиалы',
      url: '/crm/settings/branches',
      component: lazy(() => import('./components/Settings/CrmBranchesPage')),
      hiddenFromMenu: true,
    },
    {
      icon: IconCategory,
      label: 'Сегменты',
      url: '/crm/settings/segments',
      component: lazy(() => import('./components/Settings/CrmSegmentsPage')),
      hiddenFromMenu: true,
    },
    {
      icon: IconBroadcast,
      label: 'Источники',
      url: '/crm/settings/sources',
      component: lazy(() => import('./components/Settings/CrmLeadSourcesPage')),
      hiddenFromMenu: true,
    },
    {
      icon: IconTag,
      label: 'Теги',
      url: '/crm/settings/tags',
      component: lazy(() => import('./components/Settings/CrmTagsPage')),
      hiddenFromMenu: true,
    },
    {
      icon: IconSettings,
      label: 'Настройки типов сделок',
      url: '/crm/settings/deal-types',
      component: lazy(() => import('./components/Settings/DealTypeSettingsPage')),
      hiddenFromMenu: true,
    },
    {
      icon: IconSettings,
      label: 'Настройки типов лидов',
      url: '/crm/settings/lead-types',
      component: lazy(() => import('./components/Settings/LeadTypeSettingsPage')),
      hiddenFromMenu: true,
    },
    {
      icon: IconMap,
      label: 'Регионы продаж',
      url: '/crm/settings/sales-areas',
      component: lazy(() => import('./components/Settings/CrmSalesAreasPage')),
      hiddenFromMenu: true,
    },
    {
      icon: IconSettings,
      label: 'Типы партнёров',
      url: '/crm/settings/partner-roles',
      component: lazy(() => import('./components/Settings/PartnerRoleSettingsPage')),
      hiddenFromMenu: true,
    },
    {
      icon: IconSettings,
      label: 'Статусы партнёров',
      url: '/crm/settings/partner-statuses',
      component: lazy(() => import('./components/Settings/CrmPartnerStatusesPage')),
      hiddenFromMenu: true,
    },
  ]);
}
